import { createCommonStore } from './index';
import { useUserInfoStore } from './use-user-info-store';

interface CollapsableListState {
  expandedItemIds: number[];
  page: number;
  pageSize: number;
}

interface FilterStore {
  filters: Record<string, Record<string, Record<string, string>>>; // { [accountCode]: { [page]: { filters } } }
  collapsableList: Record<string, Record<string, CollapsableListState>>; // { [accountCode]: { [page]: state } }
  setFilters: (page: string, filters: Record<string, string>) => void;
  clearFilters: (page: string) => void;
  syncWithUrl: (page: string, urlFilters: Record<string, string>) => void;
  updateUrl: (page: string) => void;
  setCollapsableList: (page: string, state: CollapsableListState) => void;
  clearCollapsableList: (page: string) => void;
}

const getSelectedAccountCode = () => {
  const { selectedAccount } = useUserInfoStore.getState();
  return selectedAccount?.code;
};

const updateNestedState = <T extends Record<string, any>>(
  state: Record<string, Record<string, T>>,
  accountCode: string,
  page: string,
  value: T | null,
): Record<string, Record<string, T>> => {
  const updatedState = { ...state };

  if (value === null) {
    if (updatedState[accountCode]) {
      delete updatedState[accountCode][page];
      if (Object.keys(updatedState[accountCode]).length === 0) {
        delete updatedState[accountCode];
      }
    }
  } else {
    updatedState[accountCode] = {
      ...(updatedState[accountCode] || {}),
      [page]: value,
    };
  }

  return updatedState;
};

export const useFiltersStore = createCommonStore<FilterStore>(
  (set) => {
    const getState = () => useFiltersStore.getState();

    return {
      filters: {},
      collapsableList: {},

      setFilters: (page, filters) => {
        const accountCode = getSelectedAccountCode();
        if (!accountCode) {
          return;
        }

        set((state) => ({
          filters: updateNestedState(state.filters, accountCode, page, filters),
        }));

        getState().updateUrl(page);
      },

      clearFilters: (page) => {
        const accountCode = getSelectedAccountCode();
        if (!accountCode) {
          return;
        }

        set((state) => ({
          filters: updateNestedState(state.filters, accountCode, page, null),
        }));

        getState().updateUrl(page);
      },

      syncWithUrl: (page, urlFilters) => {
        const accountCode = getSelectedAccountCode();
        if (!accountCode) {
          return;
        }

        set((state) => ({
          filters: updateNestedState(state.filters, accountCode, page, urlFilters),
        }));
      },

      setCollapsableList: (page, state) => {
        const accountCode = getSelectedAccountCode();
        if (!accountCode) {
          return;
        }

        set((store) => ({
          collapsableList: updateNestedState(store.collapsableList, accountCode, page, state),
        }));

        getState().updateUrl(page);
      },

      clearCollapsableList: (page) => {
        const accountCode = getSelectedAccountCode();
        if (!accountCode) {
          return;
        }

        set((store) => ({
          collapsableList: updateNestedState(store.collapsableList, accountCode, page, null),
        }));

        getState().updateUrl(page);
      },

      updateUrl: (page) => {
        const accountCode = getSelectedAccountCode();
        if (!accountCode) {
          return;
        }

        const filters = getState().filters[accountCode]?.[page] || {};
        const collapsableList = getState().collapsableList[accountCode]?.[page];
        const url = new URL(window.location.href);

        const updateUrlParams = (params: Record<string, string | number>) => {
          Object.entries(params).forEach(([key, value]) => {
            if (value) {
              url.searchParams.set(key, String(value));
            } else {
              url.searchParams.delete(key);
            }
          });
        };

        updateUrlParams(filters);

        if (collapsableList) {
          updateUrlParams({
            expanded: collapsableList.expandedItemIds.join(','),
            page: collapsableList.page,
            pageSize: collapsableList.pageSize,
          });
        }

        window.history.replaceState({}, '', url.toString());
      },
    };
  },
  { name: 'filters-store', storage: 'session' },
);
